import type { ProcessedApplicant, SortField, SortDirection } from "./customTypes"
import { CANDIDATE_CATEGORIES } from "./constants"
import { Candidate } from '../../../openapi/api/model/candidate';

export function processApplicants(
  data: Candidate[],
  localNotes: Record<string, string>,
  localHiddenStates: Record<string, boolean>,
): ProcessedApplicant[] {
  return data.map((applicant) => {
    let category = "Failed pre-qualification"

    if (applicant.qualified) {
      category = applicant.case?.decision || "No Match"
    }

    return {
      ...applicant,
      category,
      categoryInfo: CANDIDATE_CATEGORIES[category],
      note: localNotes[applicant._id] !== undefined ? localNotes[applicant._id] : applicant.case?.note || "",
      isHidden:
        localHiddenStates[applicant._id] !== undefined
          ? localHiddenStates[applicant._id]
          : applicant.case?.isInvisible || false,
    }
  })
}

export function filterApplicants(
  applicants: ProcessedApplicant[],
  searchTerm: string,
  showOnlyQualified: boolean,
  showHidden: boolean,
  categoryFilter: string | null,
): ProcessedApplicant[] {
  return applicants.filter((applicant) => {
    const matchesSearch = Object.values(applicant).some(
      (value) => value && typeof value === "string" && value.toLowerCase().includes(searchTerm.toLowerCase()),
    )

    const matchesQualified = showOnlyQualified ? applicant.qualified : true
    const matchesCategory = categoryFilter ? applicant.category === categoryFilter : true

    // Fixed logic: when showHidden is true, show only hidden candidates
    // when showHidden is false, show only visible candidates
    const matchesHidden = showHidden ? applicant.isHidden : !applicant.isHidden

    return matchesSearch && matchesQualified && matchesCategory && matchesHidden
  })
}

export function sortApplicants(
  applicants: ProcessedApplicant[],
  sortBy: SortField,
  sortDirection: SortDirection,
): ProcessedApplicant[] {
  return [...applicants].sort((a, b) => {
    if (sortBy === "category") {
      const priorityA = a.categoryInfo?.priority || 999
      const priorityB = b.categoryInfo?.priority || 999
      return sortDirection === "asc" ? priorityA - priorityB : priorityB - priorityA
    } else if (sortBy === "name") {
      const nameA = `${a.firstName} ${a.lastName}`.toLowerCase()
      const nameB = `${b.firstName} ${b.lastName}`.toLowerCase()
      return sortDirection === "asc" ? nameA.localeCompare(nameB) : nameB.localeCompare(nameA)
    } else if (sortBy === "email") {
      const emailA = a.email?.toLowerCase() || ""
      const emailB = b.email?.toLowerCase() || ""
      return sortDirection === "asc" ? emailA.localeCompare(emailB) : emailB.localeCompare(emailA)
    }
    return 0
  })
}

export function paginateApplicants(
  applicants: ProcessedApplicant[],
  currentPage: number,
  itemsPerPage: number,
): ProcessedApplicant[] {
  return applicants.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
}

export function getCategoryCounts(applicants: ProcessedApplicant[], showHidden: boolean): Record<string, number> {
  return applicants
    .filter((applicant) => (showHidden ? applicant.isHidden : !applicant.isHidden))
    .reduce(
      (acc, applicant) => {
        const category = applicant.category
        acc[category] = (acc[category] || 0) + 1
        return acc
      },
      {} as Record<string, number>,
    )
}
